'use client';

import type { ComponentProps } from 'react';

import { Tooltip as TooltipPrimitive } from '@base-ui-components/react/tooltip';
import { clsxm } from '@vs-shop/utils';

import { OverlayArrowSvg, overlayArrowSideClasses } from '../overlayArrow';

export const TooltipProvider = ({
  delay = 0,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />
);

export const Tooltip = (props: ComponentProps<typeof TooltipPrimitive.Root>) => (
  <TooltipPrimitive.Root data-slot="tooltip" {...props} />
);

export const TooltipTrigger = (props: ComponentProps<typeof TooltipPrimitive.Trigger>) => (
  <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
);

// Base UI's Tooltip.Arrow renders a positioned <div>; the triangle + per-side
// placement are shared with Popover via overlayArrow.
export const TooltipArrow = ({
  className,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Arrow>) => (
  <TooltipPrimitive.Arrow
    data-slot="tooltip-arrow"
    className={clsxm(overlayArrowSideClasses, className)}
    {...props}
  >
    <OverlayArrowSvg />
  </TooltipPrimitive.Arrow>
);

export type TooltipContentProps = ComponentProps<typeof TooltipPrimitive.Popup> &
  Pick<
    ComponentProps<typeof TooltipPrimitive.Positioner>,
    'side' | 'sideOffset' | 'align' | 'alignOffset'
  > & { showArrow?: boolean };

export const TooltipContent = ({
  className,
  side,
  sideOffset = 8,
  align,
  alignOffset,
  showArrow = true,
  children,
  ...props
}: TooltipContentProps) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Positioner
      data-slot="tooltip-positioner"
      className="z-50"
      sideOffset={sideOffset}
      {...(side === undefined ? {} : { side })}
      {...(align === undefined ? {} : { align })}
      {...(alignOffset === undefined ? {} : { alignOffset })}
    >
      <TooltipPrimitive.Popup
        data-slot="tooltip-content"
        className={clsxm(
          'w-fit max-w-96 rounded-xs border p-4 text-xs/5 tracking-tight text-balance',
          'border-background-intense-more bg-background text-foreground shadow-xl',
          'origin-(--transform-origin) transition-[transform,scale,opacity] duration-150',
          'data-starting-style:scale-95 data-starting-style:opacity-0',
          'data-ending-style:scale-95 data-ending-style:opacity-0',
          className
        )}
        {...props}
      >
        {showArrow ? <TooltipArrow /> : null}
        {children}
      </TooltipPrimitive.Popup>
    </TooltipPrimitive.Positioner>
  </TooltipPrimitive.Portal>
);
